import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spinner, Alert } from "reactstrap";
import { toast } from "react-toastify";
import api from "../../api/index";

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    handleOAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOAuthCallback = async () => {
    try {
      // Get OAuth parameters from URL
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const errorParam = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      // Check for OAuth errors
      if (errorParam) {
        const errorMsg = errorDescription || errorParam;
        setError(errorMsg);
        toast.error(`OAuth failed: ${errorMsg}`);
        setProcessing(false);
        // Redirect back to setup after showing error
        setTimeout(() => {
          navigate("/wallet/setup");
        }, 3000);
        return;
      }

      // Validate required parameters
      if (!code || !state) {
        // User likely clicked "Go Back" without completing login
        setError("OAuth authentication was cancelled or incomplete");
        toast.warning("Authentication cancelled. Returning to setup...");
        setProcessing(false);
        // Redirect back to setup immediately
        setTimeout(() => {
          navigate("/wallet/setup");
        }, 2000);
        return;
      }

      // Get the redirect URI from current URL origin
      const redirectUri = `${window.location.origin}/wallet/setup/callback`;

      // Send authorization code to backend for verification
      const response = await api.postRequest("/1.0/wallet/verify", {
        code,
        state,
        redirectUri,
      });

      if (response.status === 200) {
        toast.success("Successfully connected to Airwallex!");
        // Redirect to wallet setup page
        setTimeout(() => {
          navigate("/wallet/setup?status=callback_success");
        }, 1000);
      } else {
        throw new Error(response.data?.message || "Verification failed");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to complete OAuth process";
      setError(errorMessage);
      toast.error(errorMessage);
      setProcessing(false);
      // Redirect back to setup after showing error
      setTimeout(() => {
        navigate("/wallet/setup");
      }, 3000);
    }
  };

  if (processing && !error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f7fa",
        }}
      >
        <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
        <h3 style={{ marginTop: "24px", color: "#2c3e50" }}>
          Completing Airwallex Connection...
        </h3>
        <p style={{ color: "#7f8c8d", marginTop: "8px" }}>
          Please wait while we process your authorization
        </p>
      </div>
    );
  }

  // Show error state if processing stopped with error
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f7fa",
          padding: "24px",
        }}
      >
        <Alert color="warning" style={{ maxWidth: "600px", width: "100%" }}>
          <h4>Authentication Cancelled</h4>
          <p>{error}</p>
          <p style={{ marginTop: "12px", fontSize: "14px", color: "#7f8c8d" }}>
            Redirecting you back to setup...
          </p>
        </Alert>
      </div>
    );
  }

  // This should never be reached, but return null as fallback
  return null;
};

export default OAuthCallback;
